/**
 * Google ADK Adapter — implements SdkAdapter for @google/adk (ADK-TS / adk-js)
 *
 * Replaces the raw @google/genai adapter. ADK provides a native subagent
 * mechanism (`LlmAgent.subAgents` + description-based agent transfer), which is
 * required for model B (LLM-internal routing): the entry agent's LLM delegates
 * to candidate sub-agents registered in the agent tree.
 *
 * Supports:
 * - send()          — build a root LlmAgent (+ candidate subAgents), run once,
 *                     return the final response text
 * - sendExecution() — rich path using full contract context
 *
 * No followUp(): ADK's InMemoryRunner uses ephemeral sessions, so the runtime's
 * recovery loop falls back to a fresh send() with reconstructed context.
 *
 * API used (verified against the installed package's type definitions under
 * node_modules/@google/adk/dist/types):
 *   - class LlmAgent (agents/llm_agent.d.ts): constructor(config) where config
 *     extends BaseAgentConfig { name; description?; subAgents?: BaseAgent[] }
 *     and adds { model?: string | BaseLlm; instruction?: string; tools? }.
 *   - class InMemoryRunner (runner/in_memory_runner.d.ts): constructor({ agent,
 *     appName? }); inherits runEphemeral({ userId, newMessage: Content })
 *     returning AsyncGenerator<Event> (runner/runner.d.ts).
 *   - isFinalResponse(event) / stringifyContent(event) (events/event.d.ts).
 *   - class Gemini (models/google_llm.d.ts): constructor({ model, apiKey })
 *     — used to inject an explicit API key; otherwise ADK reads
 *     GOOGLE_GENAI_API_KEY / GEMINI_API_KEY from the environment.
 */

import type {
  SdkAdapter,
  AdapterSendOptions,
  AgentExecutionRequest,
  AgentProgressEvent,
  CacheConfig,
  CandidateAgent,
} from "../lib/task-runner.js";
import type { ObserverEmitFn, TokenUsage } from "../lib/observability-bridge.js";
import {
  emitLlmTurnOpen,
  emitLlmTurnClose,
  emitToolExecOpen,
  emitToolExecClose,
} from "../lib/observability-bridge.js";
import { getBlockingViolations, type GuardrailHooks } from "../lib/guardrail-hooks.js";
import type { MemoryRef } from "../lib/memory-store.js";
import type { LibraryToolRegistry } from "../lib/library-tool-binding.js";
import { buildAdkFunctionTools } from "../lib/library-tool-sdk.js";

// ---------------------------------------------------------------------------
// Duck-typed SDK surface (avoid a hard compile-time dependency on @google/adk)
// ---------------------------------------------------------------------------

type AdkContentPart = {
  text?: string;
  functionCall?: { name?: string; args?: Record<string, unknown> };
  functionResponse?: { name?: string; response?: unknown };
};

type AdkContent = {
  role?: string;
  parts?: AdkContentPart[];
};

type AdkEvent = {
  author?: string;
  content?: AdkContent;
  usageMetadata?: {
    promptTokenCount?: number;
    candidatesTokenCount?: number;
    totalTokenCount?: number;
    cachedContentTokenCount?: number;
  };
  [key: string]: unknown;
};

type LlmAgentConfig = {
  name: string;
  description?: string;
  model?: string | object;
  instruction?: string;
  tools?: unknown[];
  subAgents?: unknown[];
};

type LlmAgentInstance = object;
type LlmAgentConstructor = new (config: LlmAgentConfig) => LlmAgentInstance;

type RunnerInstance = {
  runEphemeral(params: {
    userId: string;
    newMessage: AdkContent;
    stateDelta?: Record<string, unknown>;
  }): AsyncGenerator<AdkEvent, void, unknown>;
};

type InMemoryRunnerConstructor = new (params: {
  agent: LlmAgentInstance;
  appName?: string;
}) => RunnerInstance;

type GeminiConstructor = new (params: { model?: string; apiKey?: string }) => object;

interface AdkSdk {
  LlmAgent: LlmAgentConstructor;
  InMemoryRunner: InMemoryRunnerConstructor;
  isFinalResponse: (event: AdkEvent) => boolean;
  stringifyContent: (event: AdkEvent) => string;
  Gemini: GeminiConstructor;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * ADK requires agent names to be valid JS identifiers, unique within the tree,
 * and not "user". DSL agent ids may contain hyphens, so sanitize to identifiers.
 */
function sanitizeAgentName(name: string): string {
  let id = name.replace(/[^a-zA-Z0-9_]/g, "_");
  if (id.length === 0 || /^[0-9]/.test(id)) id = `agent_${id}`;
  if (id === "user") id = "user_agent";
  return id;
}

/** Concatenate text parts from an event's content. */
function extractEventText(event: AdkEvent): string {
  const parts = event.content?.parts;
  if (!parts) return "";
  return parts.map((p) => p.text ?? "").join("");
}

function truncateInputSummary(input: unknown, maxLen = 80): string {
  if (input == null) return "";
  let str: string;
  if (typeof input === "string") {
    str = input;
  } else {
    try { str = JSON.stringify(input); } catch { str = String(input); }
  }
  return str.length > maxLen ? str.slice(0, maxLen - 3) + "..." : str;
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface AdkSdkAdapterConfig {
  /** API key for the Gemini API. Falls back to GOOGLE_GENAI_API_KEY / GEMINI_API_KEY env. */
  apiKey?: string;
  /** Gemini model identifier (e.g. "gemini-2.5-flash", "gemini-2.5-pro"). */
  model?: string;
  /** Root agent name (must be a JS identifier). Defaults to "root_agent". */
  rootAgentName?: string;
  /** Runtime guardrail hooks. Applied as input-level checks before agent execution. */
  guardrailHooks?: GuardrailHooks;
  /** Prompt caching configuration. Enabled by default. */
  cacheConfig?: CacheConfig;
}

// ---------------------------------------------------------------------------
// Adapter
// ---------------------------------------------------------------------------

export class AdkSdkAdapter implements SdkAdapter {
  private apiKey: string | undefined;
  private model: string;
  private rootAgentName: string;
  private guardrailHooks: GuardrailHooks | undefined;
  private cacheConfig: CacheConfig;
  private lastMemoryRef: MemoryRef | null = null;
  private lastTokenUsage: TokenUsage | null = null;
  private runCounter = 0;

  private sdk: AdkSdk | null = null;

  constructor(config: AdkSdkAdapterConfig = {}) {
    this.apiKey = config.apiKey;
    this.model = config.model ?? "gemini-2.5-flash";
    this.rootAgentName = config.rootAgentName ?? "root_agent";
    this.guardrailHooks = config.guardrailHooks;
    this.cacheConfig = config.cacheConfig ?? { enabled: true };
  }

  // -------------------------------------------------------------------------
  // Internal helpers
  // -------------------------------------------------------------------------

  private async resolveSdk(): Promise<AdkSdk> {
    if (this.sdk) return this.sdk;

    const mod = (await import("@google/adk")) as unknown as AdkSdk;
    this.sdk = {
      LlmAgent: mod.LlmAgent,
      InMemoryRunner: mod.InMemoryRunner,
      isFinalResponse: mod.isFinalResponse,
      stringifyContent: mod.stringifyContent,
      Gemini: mod.Gemini,
    };
    return this.sdk;
  }

  /** Resolve the `model` field for LlmAgent — a Gemini instance when an explicit
   *  API key is supplied, otherwise the bare model id (ADK reads env keys). */
  private resolveModel(sdk: AdkSdk): string | object {
    if (this.apiKey) {
      return new sdk.Gemini({ model: this.model, apiKey: this.apiKey });
    }
    return this.model;
  }

  /** Build the root LlmAgent with candidate sub-agents registered for transfer. */
  private buildRootAgent(
    sdk: AdkSdk,
    instruction: string,
    agents?: CandidateAgent[],
    extraTools?: unknown[],
  ): LlmAgentInstance {
    const subAgents = (agents ?? []).map((a) => {
      const config: LlmAgentConfig = {
        name: sanitizeAgentName(a.name),
        description: a.description,
        instruction: a.prompt,
        model: a.model ?? this.resolveModel(sdk),
      };
      if (a.tools) config.tools = a.tools;
      return new sdk.LlmAgent(config);
    });

    const rootConfig: LlmAgentConfig = {
      name: sanitizeAgentName(this.rootAgentName),
      instruction,
      model: this.resolveModel(sdk),
    };
    if (subAgents.length > 0) rootConfig.subAgents = subAgents;
    if (extraTools && extraTools.length > 0) rootConfig.tools = extraTools;

    return new sdk.LlmAgent(rootConfig);
  }

  private async resolveLibraryTools(
    libraryTools?: LibraryToolRegistry,
  ): Promise<unknown[]> {
    if (!libraryTools || libraryTools.getAll().length === 0) return [];
    try {
      return await buildAdkFunctionTools(libraryTools);
    } catch {
      return [];
    }
  }

  private async runAgent(
    instruction: string,
    userMessage: string,
    agents: CandidateAgent[] | undefined,
    onProgress?: (event: AgentProgressEvent) => void,
    libraryTools?: LibraryToolRegistry,
    observer?: ObserverEmitFn,
    parentSpanId?: string,
    agentSessionId?: string,
  ): Promise<{ text: string; tokenUsage: TokenUsage | null }> {
    if (this.guardrailHooks) {
      const results = this.guardrailHooks.runChecks({ command: userMessage });
      const blocking = getBlockingViolations(results);
      if (blocking.length > 0) {
        const msg = blocking.map((r) => `[${r.guardrail_id}] ${r.message}`).join("\n");
        throw new Error(msg);
      }
    }

    const sdk = await this.resolveSdk();
    const extraTools = await this.resolveLibraryTools(libraryTools);
    const root = this.buildRootAgent(sdk, instruction, agents, extraTools);
    const runner = new sdk.InMemoryRunner({ agent: root });

    let finalText = "";
    const stream = runner.runEphemeral({
      userId: "agent-runtime",
      newMessage: { role: "user", parts: [{ text: userMessage }] },
    });

    let turnNumber = 0;
    let currentTurnSpanId: string | undefined;
    let currentTurnStart = 0;
    let toolCallsInTurn = 0;
    let turnInputTokens = 0;
    let turnOutputTokens = 0;
    const openToolSpans: Array<{ spanId: string; toolName: string; startMs: number }> = [];

    let totalInputTokens = 0;
    let totalOutputTokens = 0;
    let totalTokens = 0;
    let hasUsageData = false;

    const closePendingToolSpans = (): void => {
      for (const ts of openToolSpans) {
        emitToolExecClose(observer, ts.spanId, ts.toolName, Date.now() - ts.startMs, agentSessionId);
      }
      openToolSpans.length = 0;
    };

    const closeCurrentTurn = (): void => {
      if (currentTurnSpanId) {
        emitLlmTurnClose(observer, currentTurnSpanId, turnNumber, Date.now() - currentTurnStart, {
          inputTokens: turnInputTokens || undefined,
          outputTokens: turnOutputTokens || undefined,
          toolCallsCount: toolCallsInTurn || undefined,
        }, agentSessionId);
        currentTurnSpanId = undefined;
      }
    };

    const captureUsage = (usageMetadata: {
      promptTokenCount?: number;
      candidatesTokenCount?: number;
      totalTokenCount?: number;
    }): void => {
      const inTok = typeof usageMetadata.promptTokenCount === "number"
        ? usageMetadata.promptTokenCount
        : 0;
      const outTok = typeof usageMetadata.candidatesTokenCount === "number"
        ? usageMetadata.candidatesTokenCount
        : 0;
      if (inTok > 0 || outTok > 0) {
        totalInputTokens += inTok;
        totalOutputTokens += outTok;
        turnInputTokens += inTok;
        turnOutputTokens += outTok;
        hasUsageData = true;
      }
      if (typeof usageMetadata.totalTokenCount === "number") {
        totalTokens += usageMetadata.totalTokenCount;
      }
    };

    for await (const event of stream) {
      if (onProgress) emitProgressFromAdkEvent(event, onProgress);

      const ev = event as Record<string, unknown>;
      if (ev.errorCode || ev.errorMessage) {
        closePendingToolSpans();
        closeCurrentTurn();
        throw new Error(
          `Gemini API error (${ev.errorCode ?? "unknown"}): ${ev.errorMessage ?? "no details"}`,
        );
      }

      const role = event.content?.role;
      const parts = event.content?.parts;

      if (role === "model" && parts && parts.length > 0) {
        closePendingToolSpans();
        closeCurrentTurn();
        turnNumber++;
        toolCallsInTurn = 0;
        turnInputTokens = 0;
        turnOutputTokens = 0;
        currentTurnStart = Date.now();
        currentTurnSpanId = emitLlmTurnOpen(observer, parentSpanId, turnNumber, this.model, undefined, agentSessionId);

        for (const part of parts) {
          if (part.functionCall?.name) {
            toolCallsInTurn++;
            const toolName = part.functionCall.name;
            const summary = truncateInputSummary(part.functionCall.args);
            const toolSpanId = emitToolExecOpen(observer, currentTurnSpanId, toolName, summary, turnNumber, agentSessionId);
            if (toolSpanId) {
              openToolSpans.push({ spanId: toolSpanId, toolName, startMs: Date.now() });
            }
          }
        }
      } else if (parts) {
        for (const part of parts) {
          if (part.functionResponse?.name) {
            if (openToolSpans.length > 0) {
              const ts = openToolSpans.shift()!;
              emitToolExecClose(observer, ts.spanId, ts.toolName, Date.now() - ts.startMs, agentSessionId);
            }
          }
        }
      }

      if (event.usageMetadata) {
        captureUsage(event.usageMetadata);
      }

      if (sdk.isFinalResponse(event) && event.content) {
        const text = sdk.stringifyContent(event) || extractEventText(event);
        if (text) finalText = text;
        closePendingToolSpans();
        closeCurrentTurn();
      }
    }

    closePendingToolSpans();
    closeCurrentTurn();

    const tokenUsage: TokenUsage | null = hasUsageData
      ? {
          inputTokens: totalInputTokens,
          outputTokens: totalOutputTokens,
          totalTokens: totalTokens || (totalInputTokens + totalOutputTokens),
        }
      : null;

    return { text: finalText, tokenUsage };
  }

  private nextMemoryRef(): MemoryRef {
    return {
      id: `adk-run-${++this.runCounter}-${Date.now()}`,
      provider: "google-adk",
      compat: `google-adk@${this.model}`,
      created_at: new Date().toISOString(),
    };
  }

  // -------------------------------------------------------------------------
  // SdkAdapter interface
  // -------------------------------------------------------------------------

  async send(prompt: string, options: AdapterSendOptions): Promise<string> {
    this.lastMemoryRef = null;
    this.lastTokenUsage = null;

    const split = options.splitPrompt;
    let instruction = "";
    let userMessage: string;

    if (split && this.cacheConfig.enabled !== false) {
      instruction = split.system.join("\n\n---\n\n");
      userMessage = split.user;
    } else {
      userMessage = prompt;
    }

    const { text, tokenUsage } = await this.runAgent(instruction, userMessage, options.agents, options.onProgress, options.libraryTools, options.observer, options.parentSpanId, options.sessionId);
    this.lastMemoryRef = this.nextMemoryRef();
    this.lastTokenUsage = tokenUsage;
    return text;
  }

  async sendExecution(request: AgentExecutionRequest): Promise<string> {
    this.lastMemoryRef = null;
    this.lastTokenUsage = null;

    const split = request.splitPrompt;
    let instruction = "";
    let userMessage: string;

    if (split && this.cacheConfig.enabled !== false) {
      instruction = split.system.join("\n\n---\n\n");
      userMessage = split.user;
    } else {
      userMessage = request.prompt;
    }

    const agents = request.options.agents ?? request.agents;
    const { text, tokenUsage } = await this.runAgent(instruction, userMessage, agents, request.options.onProgress, request.options.libraryTools, request.options.observer, request.options.parentSpanId, request.options.sessionId);
    this.lastMemoryRef = this.nextMemoryRef();
    this.lastTokenUsage = tokenUsage;
    return text;
  }

  getLastMemoryRef(): MemoryRef | null {
    return this.lastMemoryRef;
  }

  getLastTokenUsage(): TokenUsage | null {
    return this.lastTokenUsage;
  }

  isCompatible(compat: string): boolean {
    return compat.startsWith("google-adk@");
  }

  // -------------------------------------------------------------------------
  // Test support
  // -------------------------------------------------------------------------

  /**
   * Inject the resolved ADK SDK surface for testing.
   * Bypasses the dynamic import of @google/adk and the API key requirement.
   */
  static withSdk(sdk: AdkSdk, config?: AdkSdkAdapterConfig): AdkSdkAdapter {
    const adapter = new AdkSdkAdapter(config);
    adapter.sdk = sdk;
    return adapter;
  }
}

// ---------------------------------------------------------------------------
// Progress event mapping
// ---------------------------------------------------------------------------

function emitProgressFromAdkEvent(
  event: AdkEvent,
  onProgress: (event: AgentProgressEvent) => void,
): void {
  const parts = event.content?.parts;
  if (!parts) return;
  for (const part of parts) {
    if (part.functionCall?.name) {
      onProgress({ type: "tool_use", tool_name: part.functionCall.name, input: part.functionCall.args });
    } else if (part.functionResponse?.name) {
      onProgress({ type: "tool_result", tool_name: part.functionResponse.name });
    } else if (part.text) {
      onProgress({ type: "text", message: part.text });
    }
  }
}
